/**
 * DeliveryWhiteboard - Weekly delivery scheduling component
 * Allows drag-and-drop job scheduling and note management
 * Features: Color-coded note types, Print-friendly view
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { 
  ArrowRight, Calendar, ChevronLeft, ChevronRight, Check, 
  Clipboard, GripVertical, RefreshCw, Truck, X, Printer,
  AlertTriangle, Clock, Package, Star, Info, MapPin, Store, Hourglass
} from "lucide-react";
import { isClickCollect, requestTypeLabel, doneLabel, formatWaitingSince } from "./requestTypes";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Note type definitions with colors
const NOTE_TYPES = {
  general: { 
    label: 'General', 
    color: 'amber',
    bgClass: 'bg-amber-950/40 border-amber-700/30',
    textClass: 'text-amber-200/90',
    iconClass: 'text-amber-600/50',
    ringClass: 'ring-amber-500/50',
    printBg: '#fef3c7',
    icon: Info
  },
  urgent: { 
    label: 'Urgent', 
    color: 'red',
    bgClass: 'bg-red-950/40 border-red-700/30',
    textClass: 'text-red-200/90',
    iconClass: 'text-red-600/50',
    ringClass: 'ring-red-500/50',
    printBg: '#fee2e2',
    icon: AlertTriangle
  },
  collection: { 
    label: 'Collection', 
    color: 'blue',
    bgClass: 'bg-blue-950/40 border-blue-700/30',
    textClass: 'text-blue-200/90',
    iconClass: 'text-blue-600/50',
    ringClass: 'ring-blue-500/50',
    printBg: '#dbeafe',
    icon: Package
  },
  timed: { 
    label: 'Timed', 
    color: 'purple',
    bgClass: 'bg-purple-950/40 border-purple-700/30',
    textClass: 'text-purple-200/90',
    iconClass: 'text-purple-600/50',
    ringClass: 'ring-purple-500/50',
    printBg: '#ede9fe',
    icon: Clock
  },
  priority: { 
    label: 'Priority', 
    color: 'green',
    bgClass: 'bg-green-950/40 border-green-700/30',
    textClass: 'text-green-200/90',
    iconClass: 'text-green-600/50',
    ringClass: 'ring-green-500/50',
    printBg: '#dcfce7',
    icon: Star
  }
};

// Sound notification helper (silent - uses WebAudioAPI if available)
const SoundNotification = {
  success: () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.value = 0.1;
      o.frequency.value = 600;
      o.start();
      o.stop(ctx.currentTime + 0.1);
    } catch(e) {
      // Audio playback may fail due to browser autoplay policies - non-critical
    }
  },
  click: () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.value = 0.05;
      o.frequency.value = 800;
      o.start();
      o.stop(ctx.currentTime + 0.05);
    } catch(e) {
      // Audio playback may fail due to browser autoplay policies - non-critical
    }
  }
};

// Note Entry Input Component with type selection
const NoteEntryInput = ({ onAdd }) => {
  const [text, setText] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [isAdding, setIsAdding] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    
    const success = await onAdd(text, noteType);
    if (success) {
      setText('');
      setNoteType('general');
      setIsAdding(false);
    }
  };

  if (!isAdding) {
    return (
      <button
        onClick={() => setIsAdding(true)}
        className="text-xs text-cyan-500 hover:text-cyan-400 flex items-center gap-1"
      >
        + Add
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className="flex gap-1">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-100"
          placeholder="Add note..."
          autoFocus
        />
        <button type="submit" className="text-cyan-500 hover:text-cyan-400 px-1">
          <Check className="w-3 h-3" />
        </button>
        <button type="button" onClick={() => setIsAdding(false)} className="text-zinc-500 hover:text-zinc-400 px-1">
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {Object.entries(NOTE_TYPES).map(([type, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={type}
              type="button"
              onClick={() => setNoteType(type)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                noteType === type 
                  ? `${config.bgClass} ${config.textClass} ring-1 ${config.ringClass}`
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              <Icon className="w-2.5 h-2.5" />
              {config.label}
            </button>
          );
        })}
      </div>
    </form>
  );
};

// Note Entry Card Component with color-coding
const NoteEntryCard = ({ note, onUpdate, onDelete, onTypeChange, dragHandleProps, isPrintMode }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(note.text);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  
  const noteType = note.type || 'general';
  const typeConfig = NOTE_TYPES[noteType] || NOTE_TYPES.general;
  const TypeIcon = typeConfig.icon;

  const handleSave = async () => {
    if (text.trim() && text !== note.text) {
      await onUpdate(text);
    }
    setIsEditing(false);
  };

  const handleTypeChange = async (newType) => {
    if (newType !== noteType) {
      await onTypeChange(newType);
    }
    setShowTypeMenu(false);
  };

  // Print mode styling
  if (isPrintMode) {
    return (
      <div 
        className="rounded px-2 py-1 border text-xs"
        style={{ backgroundColor: typeConfig.printBg, borderColor: typeConfig.printBg }}
      >
        <div className="flex items-center gap-1">
          <TypeIcon className="w-3 h-3 text-zinc-600" />
          <span className="text-zinc-800">{note.text}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`${typeConfig.bgClass} border rounded px-2 py-1.5 group relative`}>
      <div className="flex items-start gap-2">
        <div {...dragHandleProps} className="cursor-grab mt-0.5">
          <GripVertical className={`w-3 h-3 ${typeConfig.iconClass}`} />
        </div>
        
        <button 
          onClick={() => setShowTypeMenu(!showTypeMenu)}
          className={`mt-0.5 ${typeConfig.iconClass} hover:opacity-80`}
          title={`Type: ${typeConfig.label}`}
        >
          <TypeIcon className="w-3 h-3" />
        </button>
        
        {isEditing ? (
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-100"
            autoFocus
          />
        ) : (
          <p 
            className={`flex-1 text-xs ${typeConfig.textClass} cursor-pointer hover:opacity-80`}
            onClick={() => setIsEditing(true)}
          >
            {note.text}
          </p>
        )}
        
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 transition-opacity"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      
      {/* Type selection dropdown */}
      {showTypeMenu && (
        <div className="absolute left-6 top-full mt-1 bg-zinc-800 border border-zinc-700 rounded shadow-lg z-10 py-1">
          {Object.entries(NOTE_TYPES).map(([type, config]) => {
            const Icon = config.icon;
            return (
              <button
                key={type}
                onClick={() => handleTypeChange(type)}
                className={`w-full flex items-center gap-2 px-3 py-1 text-xs hover:bg-zinc-700 ${
                  type === noteType ? config.textClass : 'text-zinc-300'
                }`}
              >
                <Icon className="w-3 h-3" />
                {config.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DeliveryWhiteboard = ({ isOpen, onClose, onViewJobDetail }) => {
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  });
  const [whiteboard, setWhiteboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPrintMode, setIsPrintMode] = useState(false);
  const printRef = useRef(null);
  
  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday' };

  const getDayDate = (dayName) => {
    const dayIndex = DAYS.indexOf(dayName);
    const monday = new Date(weekStart);
    const date = new Date(monday);
    date.setDate(monday.getDate() + dayIndex);
    return date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
  };

  const formatWeekDisplay = () => {
    const monday = new Date(weekStart);
    return `Week of ${monday.toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  };

  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    setWeekStart(monday.toISOString().split('T')[0]);
  };

  useEffect(() => {
    if (isOpen) {
      fetchWhiteboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, weekStart]);

  const fetchWhiteboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/delivery-whiteboard/${weekStart}`,
        { withCredentials: true }
      );
      setWhiteboard(response.data.whiteboard);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch whiteboard:', err);
      }
      toast.error('Failed to load delivery whiteboard');
    } finally {
      setLoading(false);
    }
  };

  const removeJob = async (dayName, jobId) => {
    try {
      await axios.delete(
        `${API}/delivery-whiteboard/${weekStart}/day/${dayName}/jobs/${encodeURIComponent(jobId)}`,
        { withCredentials: true }
      );
      setWhiteboard(prev => ({
        ...prev,
        days: {
          ...prev.days,
          [dayName]: {
            ...prev.days[dayName],
            jobs: prev.days[dayName].jobs.filter(j => j.job_id !== jobId)
          }
        }
      }));
      toast.success('Job removed from delivery schedule');
    } catch (err) {
      console.error('Failed to remove job:', err);
      toast.error(err.response?.data?.detail || 'Failed to remove job');
    }
  };

  const moveJob = async (jobId, fromDay, toDay) => {
    if (fromDay === toDay) return;

    try {
      await axios.post(
        `${API}/delivery-whiteboard/${weekStart}/move-job`,
        { job_id: jobId, from_day: fromDay, to_day: toDay },
        { withCredentials: true }
      );
      fetchWhiteboard();
      toast.success(`Job moved to ${DAY_LABELS[toDay]}`);
    } catch (err) {
      console.error('Failed to move job:', err);
      toast.error(err.response?.data?.detail || 'Failed to move job');
      fetchWhiteboard();
    }
  };

  const toggleDelivered = async (dayName, jobId, currentStatus) => {
    try {
      await axios.put(
        `${API}/delivery-whiteboard/${weekStart}/day/${dayName}/jobs/${encodeURIComponent(jobId)}/delivered`,
        { delivered: !currentStatus },
        { withCredentials: true }
      );
      fetchWhiteboard();
      SoundNotification.success();
      toast.success(!currentStatus ? 'Marked as delivered!' : 'Delivery status cleared');
    } catch (err) {
      toast.error('Failed to update delivery status');
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination, draggableId, type } = result;
    if (!destination) return;
    
    if (type === 'note') {
      const dayName = source.droppableId.replace('notes-', '');
      if (source.droppableId !== destination.droppableId) return;
      if (source.index === destination.index) return;
      
      const notes = [...(whiteboard.days[dayName]?.noteEntries || [])];
      const [removed] = notes.splice(source.index, 1);
      notes.splice(destination.index, 0, removed);
      
      setWhiteboard(prev => ({
        ...prev,
        days: {
          ...prev.days,
          [dayName]: {
            ...prev.days[dayName],
            noteEntries: notes
          }
        }
      }));
      
      try {
        await axios.put(
          `${API}/delivery-whiteboard/${weekStart}/day/${dayName}/reorder-notes`,
          { note_ids: notes.map(n => n.id) },
          { withCredentials: true }
        );
      } catch (err) {
        console.error('Failed to reorder notes:', err);
        toast.error('Failed to reorder notes');
        fetchWhiteboard();
      }
      return;
    }
    
    const fromDay = source.droppableId;
    const toDay = destination.droppableId;
    
    if (fromDay !== toDay) {
      moveJob(draggableId, fromDay, toDay);
    }
  };

  const addNoteEntry = async (dayName, text, noteType = 'general') => {
    if (!text.trim()) return;

    try {
      const response = await axios.post(
        `${API}/delivery-whiteboard/${weekStart}/day/${dayName}/note-entries`,
        { text: text.trim(), type: noteType },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setWhiteboard(prev => ({
          ...prev,
          days: {
            ...prev.days,
            [dayName]: {
              ...prev.days[dayName],
              noteEntries: [...(prev.days[dayName]?.noteEntries || []), response.data.note]
            }
          }
        }));
        SoundNotification.click();
        return true;
      }
    } catch (err) {
      toast.error('Failed to add note');
      return false;
    }
  };

  const updateNoteEntry = async (dayName, noteId, text) => {
    if (!text.trim()) return;

    try {
      await axios.put(
        `${API}/delivery-whiteboard/${weekStart}/day/${dayName}/note-entries/${noteId}`,
        { text: text.trim() },
        { withCredentials: true }
      );
      
      setWhiteboard(prev => ({
        ...prev,
        days: {
          ...prev.days,
          [dayName]: {
            ...prev.days[dayName],
            noteEntries: prev.days[dayName]?.noteEntries?.map(note =>
              note.id === noteId ? { ...note, text: text.trim() } : note
            ) || []
          }
        }
      }));
      return true;
    } catch (err) {
      console.error('Failed to update note:', err);
      toast.error('Failed to update note');
      return false;
    }
  };

  const updateNoteType = async (dayName, noteId, newType) => {
    try {
      await axios.put(
        `${API}/delivery-whiteboard/${weekStart}/day/${dayName}/note-entries/${noteId}`,
        { type: newType },
        { withCredentials: true }
      );
      
      setWhiteboard(prev => ({
        ...prev,
        days: {
          ...prev.days,
          [dayName]: {
            ...prev.days[dayName],
            noteEntries: prev.days[dayName]?.noteEntries?.map(note =>
              note.id === noteId ? { ...note, type: newType } : note
            ) || []
          }
        }
      }));
      return true;
    } catch (err) {
      console.error('Failed to update note type:', err);
      toast.error('Failed to update note type');
      return false;
    }
  };

  const deleteNoteEntry = async (dayName, noteId) => {
    try {
      await axios.delete(
        `${API}/delivery-whiteboard/${weekStart}/day/${dayName}/note-entries/${noteId}`,
        { withCredentials: true }
      );
      
      setWhiteboard(prev => ({
        ...prev,
        days: {
          ...prev.days,
          [dayName]: {
            ...prev.days[dayName],
            noteEntries: prev.days[dayName]?.noteEntries?.filter(note => note.id !== noteId) || []
          }
        }
      }));
      toast.success('Note deleted');
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  // Print functionality
  const handlePrint = () => {
    setIsPrintMode(true);
    setTimeout(() => {
      window.print();
      setIsPrintMode(false);
    }, 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-zinc-950 z-50 flex flex-col" data-testid="delivery-whiteboard">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-zinc-700 hover:bg-zinc-800 rounded-sm"
          >
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" /> Back to Board
          </Button>
          <div className="flex items-center gap-2">
            <Clipboard className="w-6 h-6 text-cyan-500" />
            <h1 className="text-xl font-bold text-zinc-100 uppercase tracking-wide">
              Delivery Whiteboard
            </h1>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button onClick={() => navigateWeek(-1)} variant="outline" size="sm" className="border-zinc-700 hover:bg-zinc-800 rounded-sm">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-center min-w-[280px]">
            <p className="text-lg font-bold text-zinc-100">{formatWeekDisplay()}</p>
          </div>
          <Button onClick={() => navigateWeek(1)} variant="outline" size="sm" className="border-zinc-700 hover:bg-zinc-800 rounded-sm">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button onClick={goToCurrentWeek} variant="outline" size="sm" className="border-cyan-700 hover:bg-cyan-900/30 text-cyan-400 rounded-sm ml-2">
            <Calendar className="w-4 h-4 mr-1" /> Today
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <Button 
            onClick={handlePrint}
            variant="outline" 
            size="sm" 
            className="border-zinc-700 hover:bg-zinc-800 rounded-sm print:hidden"
          >
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <span className="text-sm text-zinc-500 print:hidden">Business Print - Internal Use</span>
        </div>
      </div>

      {/* Whiteboard Content */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
          </div>
        ) : whiteboard ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="grid grid-cols-5 gap-4 h-full min-h-[600px]">
              {DAYS.map((day) => (
                <Droppable key={day} droppableId={day}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`bg-zinc-900 border rounded-sm flex flex-col transition-colors ${
                        snapshot.isDraggingOver ? 'border-cyan-500 bg-cyan-950/20' : 'border-zinc-800'
                      }`}
                    >
                      <div className="bg-zinc-950 p-3 border-b border-zinc-800 rounded-t-sm">
                        <h3 className="text-lg font-bold text-zinc-100 uppercase tracking-wide">{DAY_LABELS[day]}</h3>
                        <p className="text-sm text-zinc-500">{getDayDate(day)}</p>
                      </div>

                      {/* Notes Area */}
                      <div className="p-3 border-b border-zinc-800 print:border-gray-300">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-zinc-500 uppercase font-bold print:text-gray-700">
                            Notes ({whiteboard.days[day]?.noteEntries?.length || 0})
                          </span>
                          {!isPrintMode && <NoteEntryInput onAdd={(text, type) => addNoteEntry(day, text, type)} />}
                        </div>
                        
                        <Droppable droppableId={`notes-${day}`} type="note">
                          {(notesProvided, notesSnapshot) => (
                            <div 
                              ref={notesProvided.innerRef}
                              {...notesProvided.droppableProps}
                              className={`space-y-2 max-h-[200px] overflow-y-auto min-h-[40px] rounded print:max-h-none print:overflow-visible ${notesSnapshot.isDraggingOver ? 'bg-amber-950/20' : ''}`}
                            >
                              {whiteboard.days[day]?.noteEntries?.map((note, noteIndex) => (
                                <Draggable key={note.id} draggableId={`note-${note.id}`} index={noteIndex} isDragDisabled={isPrintMode}>
                                  {(noteProvided, noteSnapshot) => (
                                    <div
                                      ref={noteProvided.innerRef}
                                      {...noteProvided.draggableProps}
                                      className={noteSnapshot.isDragging ? 'shadow-lg scale-105 opacity-90' : ''}
                                    >
                                      <NoteEntryCard
                                        note={note}
                                        onUpdate={(text) => updateNoteEntry(day, note.id, text)}
                                        onDelete={() => deleteNoteEntry(day, note.id)}
                                        onTypeChange={(newType) => updateNoteType(day, note.id, newType)}
                                        dragHandleProps={noteProvided.dragHandleProps}
                                        isPrintMode={isPrintMode}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {notesProvided.placeholder}
                              {(!whiteboard.days[day]?.noteEntries || whiteboard.days[day].noteEntries.length === 0) && (
                                <p className="text-xs text-zinc-600 text-center py-2 print:text-gray-500">No notes yet</p>
                              )}
                            </div>
                          )}
                        </Droppable>
                      </div>

                      {/* Jobs Area */}
                      <div className="flex-1 p-3 overflow-y-auto">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-zinc-500 uppercase font-bold">
                            Deliveries ({whiteboard.days[day]?.jobs?.length || 0})
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          {whiteboard.days[day]?.jobs?.map((job, index) => {
                            const isCC = isClickCollect(job);
                            return (
                            <Draggable key={job.job_id} draggableId={job.job_id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  data-testid={`wb-job-${job.job_id}`}
                                  className={`bg-zinc-950 border rounded-sm p-3 transition-all ${
                                    job.delivered
                                      ? 'border-emerald-600/50 bg-emerald-950/20'
                                      : isCC ? 'border-orange-500/70 border-l-4 bg-orange-950/30' : 'border-zinc-700'
                                  } ${snapshot.isDragging ? 'shadow-lg scale-105' : ''}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div {...provided.dragHandleProps} className="cursor-grab mt-1">
                                      <GripVertical className="w-4 h-4 text-zinc-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span 
                                          className={`font-mono font-bold cursor-pointer ${isCC ? 'text-orange-400 hover:text-orange-300' : 'text-cyan-400 hover:text-cyan-300'}`}
                                          onClick={() => onViewJobDetail && onViewJobDetail(job.job_id)}
                                        >
                                          #{job.job_id}
                                        </span>
                                        {isCC && (
                                          <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/40 text-[10px]" data-testid={`wb-job-type-${job.job_id}`}>
                                            {requestTypeLabel(job.request_type)}
                                          </Badge>
                                        )}
                                        {job.delivered && (
                                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">{doneLabel(job)}</Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-zinc-300 break-words">{job.customer}</p>
                                      {isCC && job.pickup_point && (
                                        <p className="text-sm text-orange-300 font-semibold break-words flex items-start gap-1" data-testid={`wb-job-pickup-${job.job_id}`}>
                                          <Store className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                          {job.pickup_point}
                                        </p>
                                      )}
                                      {job.location && (
                                        <p className="text-sm text-cyan-300 font-semibold break-words flex items-start gap-1">
                                          <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                          <span>{job.location}</span>
                                        </p>
                                      )}
                                      <p className="text-xs text-zinc-500 break-words whitespace-pre-line">{job.notes || job.product}</p>
                                      {isCC && !job.delivered && job.carried_from && (
                                        <p className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-900/50 text-red-300" data-testid={`wb-job-waiting-${job.job_id}`}>
                                          <Hourglass className="w-3 h-3" /> Waiting since {formatWaitingSince(job.carried_from)}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <Button
                                        onClick={() => toggleDelivered(day, job.job_id, job.delivered)}
                                        size="sm"
                                        title={isCC ? 'Mark collected' : 'Mark delivered'}
                                        data-testid={`wb-job-done-${job.job_id}`}
                                        className={`h-7 w-7 p-0 rounded-sm ${job.delivered ? 'bg-emerald-600 hover:bg-emerald-500' : isCC ? 'bg-orange-700 hover:bg-orange-600' : 'bg-zinc-700 hover:bg-zinc-600'}`}
                                      >
                                        <Check className="w-4 h-4" />
                                      </Button>
                                      <Button onClick={() => removeJob(day, job.job_id)} size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400 hover:bg-red-950/30" data-testid={`wb-job-remove-${job.job_id}`} aria-label="Remove from schedule">
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  <div className="mt-2 pt-2 border-t border-zinc-800">
                                    <select
                                      className="w-full bg-zinc-900 border border-zinc-700 rounded-sm text-xs p-1 text-zinc-400"
                                      value=""
                                      data-testid={`wb-job-move-${job.job_id}`}
                                      onChange={(e) => e.target.value && moveJob(job.job_id, day, e.target.value)}
                                    >
                                      <option value="">Move to...</option>
                                      {DAYS.filter(d => d !== day).map(d => (
                                        <option key={d} value={d}>{DAY_LABELS[d]}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                        
                        {(!whiteboard.days[day]?.jobs || whiteboard.days[day].jobs.length === 0) && (
                          <div className="text-center py-6 text-zinc-600">
                            <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No deliveries scheduled</p>
                            <p className="text-xs mt-1">Add jobs from Dispatch</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Droppable>
              ))}
            </div>
          </DragDropContext>
        ) : (
          <div className="text-center py-12">
            <Clipboard className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">No whiteboard data</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryWhiteboard;
